import * as Dig from "../game/dig.ts";

// The digging screen's play-through: four diggers on a strip of ground that goes down forever.
// Everything is a function of time so it can be told the same on every screen.

export type Step =
  | { readonly action: "wait"; readonly ms: number }
  | { readonly action: "ready"; readonly ms: number }
  | { readonly action: "dig"; readonly direction: Dig.Direction; readonly ms: number }
  | { readonly action: "walk"; readonly direction: Dig.Direction; readonly ms: number };

const DIG_MS = 600;
const WALK_MS = 300;

const tunnel = (direction: Dig.Direction, blows: ReadonlyArray<number>): ReadonlyArray<Step> =>
  blows.flatMap((count) => [
    ...Array.from({ length: count }, (): Step => ({ action: "dig", direction, ms: DIG_MS })),
    { action: "walk", direction, ms: WALK_MS },
  ]);

// Down three (grass, then two dirts), left three, down two, right three. Grass takes one swing.
export const INTRO: ReadonlyArray<Step> = [
  { action: "wait", ms: 900 },
  ...tunnel("down", [1, 2, 2]),
  { action: "ready", ms: 400 },
  ...tunnel("left", [2, 2, 2]),
  { action: "ready", ms: 400 },
  ...tunnel("down", [2, 2]),
  { action: "ready", ms: 400 },
  ...tunnel("right", [2, 2, 2]),
  { action: "wait", ms: 800 },
];

// The same path again, all dirt, so each cycle sinks five more rows.
export const CYCLE: ReadonlyArray<Step> = [
  { action: "wait", ms: 400 },
  ...tunnel("down", [2, 2, 2]),
  { action: "ready", ms: 400 },
  ...tunnel("left", [2, 2, 2]),
  { action: "ready", ms: 400 },
  ...tunnel("down", [2, 2]),
  { action: "ready", ms: 400 },
  ...tunnel("right", [2, 2, 2]),
  { action: "wait", ms: 400 },
];

export const INTRO_MS = INTRO.reduce((total, step) => total + step.ms, 0);
export const CYCLE_MS = CYCLE.reduce((total, step) => total + step.ms, 0);

// A dig is five frames: two of wind-up, the swing, the blow at STRIKE, and the follow-through.
export const STRIKE = 0.55;
const FOLLOW = 0.8;
const WIND_2 = 0.2;
const WIND_3 = 0.4;
export const BREATH_MS = 350;
export const STAGGER_MS = 8_000;

export const COLUMNS = 20;
export const VIEW_ROWS = 8;
export const CENTER = 4;
export const STARTS: ReadonlyArray<number> = [3, 8, 13, 18];
export const DEPTH = 5;

export type Pose =
  | { readonly action: "wait"; readonly frame: number }
  | { readonly action: "ready"; readonly frame: number }
  | { readonly action: "walk"; readonly direction: Dig.Direction; readonly frame: number }
  | { readonly action: "dig"; readonly direction: Dig.Direction; readonly frame: number };

export type Target = {
  readonly column: number;
  readonly row: number;
  readonly direction: Dig.Direction;
};

// `slot` picks which of the four a digger is drawn as.
export type Digger = {
  readonly slot: number;
  readonly column: number;
  readonly row: number;
  readonly pose: Pose;
  readonly ghost: boolean;
  readonly target?: Target;
};

export type Hit = {
  readonly column: number;
  readonly row: number;
  readonly at: number;
  readonly direction: Dig.Direction;
};

// What is in view: the grid's first row and column are the world's `originRow` and
// `originColumn`, and `camera` is the world row at the top of the view.
export type Scene = {
  readonly now: number;
  readonly camera: number;
  readonly originRow: number;
  readonly originColumn: number;
  readonly grid: ReadonlyArray<ReadonlyArray<Dig.Cell>>;
  readonly diggers: ReadonlyArray<Digger>;
  readonly hits: ReadonlyArray<Hit>;
};

const step = (direction: Dig.Direction): readonly [number, number] => {
  switch (direction) {
    case "down":
      return [0, 1];
    case "left":
      return [-1, 0];
    case "right":
      return [1, 0];
    default:
      return direction satisfies never;
  }
};

const breath = (action: "wait" | "ready", within: number): Pose => ({
  action,
  frame: Math.floor(within / BREATH_MS) % 2,
});

const digFrame = (within: number, ms: number): number => {
  const progress = within / ms;
  if (progress < WIND_2) {
    return 0;
  }
  if (progress < WIND_3) {
    return 1;
  }
  if (progress < STRIKE) {
    return 2;
  }
  if (progress < FOLLOW) {
    return 3;
  }
  return 4;
};

const walkFrame = (within: number, ms: number): number =>
  Math.min(3, Math.floor((within / ms) * 4));

const clock = (timeMs: number): number =>
  timeMs >= 0 ? timeMs : ((timeMs % INTRO_MS) + INTRO_MS) % INTRO_MS;

const cameraTop = (focusRow: number): number => Math.max(0, focusRow - CENTER);

// One finished shaft-and-gallery from a standing row, eleven blocks.
const shaft = (baseColumn: number, baseRow: number): ReadonlyArray<readonly [number, number]> => [
  [baseColumn, baseRow + 1],
  [baseColumn, baseRow + 2],
  [baseColumn, baseRow + 3],
  [baseColumn - 1, baseRow + 3],
  [baseColumn - 2, baseRow + 3],
  [baseColumn - 3, baseRow + 3],
  [baseColumn - 3, baseRow + 4],
  [baseColumn - 3, baseRow + 5],
  [baseColumn - 2, baseRow + 5],
  [baseColumn - 1, baseRow + 5],
  [baseColumn, baseRow + 5],
];

const dugBy = (
  cellColumn: number,
  cellRow: number,
  startColumn: number,
  local: number,
): boolean => {
  if (local < INTRO_MS || cellRow < Dig.SURFACE) {
    return false;
  }
  const finished = 1 + Math.floor((local - INTRO_MS) / CYCLE_MS);
  const cycle = Math.floor((cellRow - Dig.SURFACE) / DEPTH);
  if (cycle < 0 || cycle >= finished) {
    return false;
  }
  for (const [c, r] of shaft(startColumn, Dig.SURFACE - 1 + cycle * DEPTH)) {
    if (c === cellColumn && r === cellRow) {
      return true;
    }
  }
  return false;
};

type Walked = {
  readonly column: number;
  readonly row: number;
  readonly pose: Pose;
  readonly target?: Target;
  readonly hits: ReadonlyArray<Hit>;
};

const walkSteps = (
  steps: ReadonlyArray<Step>,
  now: number,
  originAt: number,
  startColumn: number,
  startRow: number,
): Walked => {
  const hits: Array<Hit> = [];
  let column = startColumn;
  let row = startRow;
  let begin = 0;
  for (const current of steps) {
    const end = begin + current.ms;
    const within = now - begin;
    switch (current.action) {
      case "wait":
      case "ready":
        if (now < end) {
          return { column, row, pose: breath(current.action, within), hits };
        }
        break;
      case "dig": {
        const [dx, dy] = step(current.direction);
        const target = { column: column + dx, row: row + dy, direction: current.direction };
        const landing = originAt + begin + STRIKE * current.ms;
        if (now >= begin + STRIKE * current.ms) {
          hits.push({ ...target, at: landing });
        }
        if (now < end) {
          return {
            column,
            row,
            pose: {
              action: "dig",
              direction: current.direction,
              frame: digFrame(within, current.ms),
            },
            target,
            hits,
          };
        }
        break;
      }
      case "walk": {
        const [dx, dy] = step(current.direction);
        if (now < end) {
          const progress = within / current.ms;
          const eased = dy === 0 ? progress : progress * progress;
          return {
            column: column + dx * eased,
            row: row + dy * eased,
            pose: {
              action: "walk",
              direction: current.direction,
              frame: walkFrame(within, current.ms),
            },
            hits,
          };
        }
        column += dx;
        row += dy;
        break;
      }
      default:
        return current satisfies never;
    }
    begin = end;
  }
  return { column, row, pose: breath("wait", now - begin), hits };
};

const play = (local: number, startColumn: number): Walked => {
  if (local < INTRO_MS) {
    return walkSteps(INTRO, local, 0, startColumn, Dig.SURFACE - 1);
  }
  const cycles = Math.floor((local - INTRO_MS) / CYCLE_MS);
  const into = (local - INTRO_MS) % CYCLE_MS;
  return walkSteps(
    CYCLE,
    into,
    INTRO_MS + cycles * CYCLE_MS,
    startColumn,
    Dig.SURFACE - 1 + (cycles + 1) * DEPTH,
  );
};

const applyHit = (cell: Dig.Cell, count: number): Dig.Cell => {
  let next = cell;
  for (let i = 0; i < count; i++) {
    next = Dig.dig(next, Dig.DAMAGE);
  }
  return next;
};

// The four at once, the leader solid and the others trailing as ghosts. Only the leader's blows
// spark: the screen follows them.
export const sceneAt = (timeMs: number): Scene => {
  const now = clock(timeMs);
  const played = STARTS.map((start, index) => play(Math.max(0, now - index * STAGGER_MS), start));
  const hits = played[0].hits;
  const diggers: ReadonlyArray<Digger> = played.map((one, index) => {
    const digger = {
      slot: index,
      column: one.column,
      row: one.row,
      pose: one.pose,
      ghost: index > 0,
    };
    return one.target === undefined ? digger : { ...digger, target: one.target };
  });
  const camera = cameraTop(diggers[0].row);
  const originRow = Math.floor(camera);
  const struck = new Map<string, number>();
  for (const one of played) {
    for (const hit of one.hits) {
      const key = `${String(hit.column)},${String(hit.row)}`;
      struck.set(key, (struck.get(key) ?? 0) + 1);
    }
  }
  const grid: ReadonlyArray<ReadonlyArray<Dig.Cell>> = Array.from(
    { length: VIEW_ROWS },
    (_, row) => {
      const world = originRow + row;
      return [...Array(COLUMNS).keys()].map((column) => {
        if (
          STARTS.some((start, index) =>
            dugBy(column, world, start, Math.max(0, now - index * STAGGER_MS)),
          )
        ) {
          return Dig.AIR;
        }
        const blows = struck.get(`${String(column)},${String(world)}`) ?? 0;
        return applyHit(Dig.cellAt([], column, world), blows);
      });
    },
  );
  return { now, camera, originRow, originColumn: 0, grid, diggers, hits };
};
